/**
 * MKS-314 — instance-settings API client.
 *
 * Backend contract: `GET /api/instance-settings` (`authenticate`-only, NOT
 * admin — `backend/src/api/routes/modules/core.ts`), returning
 * `{ data: { locale, reportingCurrency, fxRateSource } }`. The non-admin gate
 * matters: every signed-in user's UI formats money and dates, so an
 * admin-only endpoint would leave everyone else on the Czech defaults.
 *
 * Writing these three settings is a different surface entirely — the admin
 * settings PATCH in `services/api/admin.ts`.
 */
import { apiGet } from './client';

/** The locales an admin may pick, mirroring `SUPPORTED_INSTANCE_LOCALES`. */
export const INSTANCE_LOCALES = ['cs-CZ', 'en-GB', 'en-US', 'de-DE', 'sk-SK'] as const;
export type InstanceLocale = (typeof INSTANCE_LOCALES)[number];

/** The rate sources, mirroring `FX_RATE_SOURCES`. */
export const FX_RATE_SOURCES = ['ecb', 'cnb'] as const;
export type FxRateSource = (typeof FX_RATE_SOURCES)[number];

export interface InstanceSettings {
  locale: string;
  reportingCurrency: string;
  fxRateSource: string;
}

export async function fetchInstanceSettings(): Promise<InstanceSettings> {
  const res = await apiGet<{ data: InstanceSettings }>('/instance-settings');
  return res.data;
}

/** Human label for a rate source, for the admin control and FX tooltips. */
export function fxRateSourceLabel(source: string): string {
  if (source === 'ecb') return 'ECB';
  if (source === 'cnb') return 'ČNB';
  return source;
}
